import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3, TrendingUp, Users, DollarSign, Ticket, Calendar,
  ArrowRight, RefreshCw, Eye, Settings, QrCode, Download,
  ChevronDown, ChevronUp, Loader2, Clock, MapPin, CheckCircle2,
  XCircle, AlertCircle, Search, Filter, Copy, FileText, Shield, Lock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format currency
const formatCurrency = (amount) => {
  return `KES ${(amount || 0).toLocaleString()}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'TBA';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Get status badge
const StatusBadge = ({ status }) => {
  const styles = {
    published: 'bg-green-100 text-green-700 border-green-200',
    draft: 'bg-gray-100 text-gray-700 border-gray-200',
    cancelled: 'bg-red-100 text-red-700 border-red-200',
    completed: 'bg-blue-100 text-blue-700 border-blue-200'
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border capitalize ${styles[status] || styles.draft}`}>
      {status}
    </span>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color = 'primary', loading }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-xl p-6 border shadow-sm"
  >
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-gray-500 text-sm">{label}</p>
        {loading ? (
          <div className="h-8 w-24 bg-gray-100 rounded animate-pulse mt-1" />
        ) : (
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        )}
        {subValue && <p className="text-sm text-gray-500 mt-1">{subValue}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
    </div>
  </motion.div>
);

// Event Row Component
const EventRow = ({ event, expanded, onToggle, onClone, onExport }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  useEffect(() => {
    if (expanded && !analytics) {
      fetchEventAnalytics();
    }
  }, [expanded]);

  const fetchEventAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const response = await fetch(`${API}/universal-events/events/${event.id}/analytics`);
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Error fetching event analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  return (
    <div className="border rounded-xl overflow-hidden bg-white" data-testid={`event-row-${event.id}`}>
      {/* Event Header Row */}
      <div 
        className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        {/* Expand Icon */}
        <button className="text-gray-400 hover:text-gray-600">
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {/* Event Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-gray-900 truncate">{event.name}</h3>
            <StatusBadge status={event.status} />
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {formatDate(event.start_date)}
            </span>
            {event.venue_name && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {event.venue_name}
              </span>
            )}
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-900">{event.total_registrations || 0}</p>
            <p className="text-xs text-gray-500">Registrations</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-900">{event.tickets_sold || 0}</p>
            <p className="text-xs text-gray-500">Tickets</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-green-600">{formatCurrency(event.total_revenue || 0)}</p>
            <p className="text-xs text-gray-500">Revenue</p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onClone(event)}
            title="Clone Event"
            data-testid={`clone-event-${event.id}`}
          >
            <Copy className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onExport(event)}
            title="Export Revenue"
            data-testid={`export-event-${event.id}`}
          >
            <FileText className="w-4 h-4" />
          </Button>
          <Link to={`/admin/events/${event.id}/analytics`}>
            <Button variant="ghost" size="sm" title="Analytics">
              <BarChart3 className="w-4 h-4" />
            </Button>
          </Link>
          <Link to={`/admin/events/${event.id}/check-in`}>
            <Button variant="ghost" size="sm" title="Check-in">
              <QrCode className="w-4 h-4" />
            </Button>
          </Link>
          <Link to={`/admin/events/${event.id}/edit`}>
            <Button variant="ghost" size="sm" title="Settings">
              <Settings className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Expanded Details */}
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="border-t bg-gray-50"
        >
          {loadingAnalytics ? (
            <div className="p-6 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : analytics ? (
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-sm text-gray-500">Total Orders</p>
                  <p className="text-xl font-bold text-gray-900">{analytics.total_orders || 0}</p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-sm text-gray-500">Checked In</p>
                  <p className="text-xl font-bold text-green-600">
                    {analytics.checked_in || 0}
                    <span className="text-sm font-normal text-gray-500 ml-1">
                      / {analytics.total_tickets || 0}
                    </span>
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-sm text-gray-500">Capacity Used</p>
                  <p className="text-xl font-bold text-gray-900">
                    {event.max_attendees ? 
                      `${Math.round(((analytics.total_tickets || 0) / event.max_attendees) * 100)}%` : 
                      'N/A'
                    }
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-sm text-gray-500">Avg. Order Value</p>
                  <p className="text-xl font-bold text-gray-900">
                    {formatCurrency(analytics.total_orders > 0 ? 
                      (analytics.total_revenue || 0) / analytics.total_orders : 0
                    )}
                  </p>
                </div>
              </div>
              
              {/* Ticket Types Breakdown */}
              {analytics.ticket_breakdown && analytics.ticket_breakdown.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Ticket Types</h4>
                  <div className="bg-white rounded-lg border overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Type</th>
                          <th className="text-center text-xs font-medium text-gray-500 px-4 py-3">Sold</th>
                          <th className="text-center text-xs font-medium text-gray-500 px-4 py-3">Available</th>
                          <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {analytics.ticket_breakdown.map((ticket, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{ticket.name}</td>
                            <td className="px-4 py-3 text-sm text-center text-gray-600">{ticket.sold}</td>
                            <td className="px-4 py-3 text-sm text-center text-gray-600">
                              {ticket.quantity ? ticket.quantity - ticket.sold : '∞'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                              {formatCurrency(ticket.revenue)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Quick Actions */}
              <div className="flex items-center gap-3 mt-6">
                <Link to={`/admin/events/${event.id}/registrations`}>
                  <Button variant="outline" size="sm">
                    <Users className="w-4 h-4 mr-2" />
                    View Registrations
                  </Button>
                </Link>
                <Link to={`/admin/events/${event.id}/import`}>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Import Attendees
                  </Button>
                </Link>
                <Link to={`/admin/events/${event.id}/staff`}>
                  <Button variant="outline" size="sm">
                    <Users className="w-4 h-4 mr-2" />
                    Manage Staff
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              No analytics data available
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

const OrganizerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cloning, setCloning] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userRoles, setUserRoles] = useState({});
  
  // Aggregate stats
  const [stats, setStats] = useState({
    totalEvents: 0,
    totalRegistrations: 0,
    totalRevenue: 0,
    totalTickets: 0,
    upcomingEvents: 0,
    checkedIn: 0
  });

  const fetchEvents = useCallback(async () => {
    try {
      // Get user email for multi-tenant filtering
      const userEmail = user?.email || 'test@michezonet.com';
      
      // Use the new my-events endpoint for tenant-aware event listing
      const response = await fetch(`${API}/universal-events/my-events?email=${encodeURIComponent(userEmail)}&include_stats=true`);
      
      if (response.ok) {
        const data = await response.json();
        const eventsList = data.events || [];
        setEvents(eventsList);
        setIsSuperAdmin(data.is_super_admin || false);
        setUserRoles(data.user_roles || {});
        
        // Calculate aggregate stats
        const now = new Date();
        const aggregated = eventsList.reduce((acc, event) => {
          acc.totalRegistrations += event.total_registrations || 0;
          acc.totalRevenue += event.total_revenue || 0;
          acc.totalTickets += event.tickets_sold || 0;
          acc.checkedIn += event.checked_in || 0;
          if (new Date(event.start_date) > now) {
            acc.upcomingEvents++;
          }
          return acc;
        }, {
          totalRegistrations: 0,
          totalRevenue: 0,
          totalTickets: 0,
          checkedIn: 0,
          upcomingEvents: 0
        });
        
        setStats({
          ...aggregated,
          totalEvents: eventsList.length
        });
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchEvents();
  };

  // Clone event handler
  const handleCloneEvent = async (event) => {
    if (cloning) return;
    
    setCloning(event.id);
    try {
      const response = await fetch(`${API}/universal-events/events/${event.id}/clone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Event cloned! New event: ${data.event.name}`);
        fetchEvents(); // Refresh list
        // Optionally navigate to edit the new event
        // navigate(`/admin/events/${data.event.id}/edit`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clone event');
      }
    } catch (err) {
      console.error('Error cloning event:', err);
      toast.error('Failed to clone event');
    } finally {
      setCloning(null);
    }
  };

  // Export revenue report
  const handleExportRevenue = async (event) => {
    try {
      const url = `${API}/universal-events/events/${event.id}/export/revenue?format=csv`;
      
      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `${event.slug}_revenue.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Revenue report downloading...');
    } catch (err) {
      console.error('Error exporting revenue:', err);
      toast.error('Failed to export revenue report');
    }
  };

  // Export all events revenue
  const handleExportAllRevenue = () => {
    const url = `${API}/universal-events/export/revenue-summary?format=csv`;
    const link = document.createElement('a');
    link.href = url;
    link.download = 'all_events_revenue.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('All events revenue report downloading...');
  };

  // Filter events
  const filteredEvents = events.filter(event => {
    const matchesSearch = event.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">Organizer Dashboard</h1>
                {isSuperAdmin && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                    <Shield className="w-3 h-3" />
                    Super Admin
                  </span>
                )}
              </div>
              <p className="text-gray-500 mt-1">
                {isSuperAdmin 
                  ? 'Viewing all events across all organizers' 
                  : `Viewing events assigned to ${user?.email || 'you'}`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {isSuperAdmin && (
                <Button
                  variant="outline"
                  onClick={handleExportAllRevenue}
                  data-testid="export-all-revenue"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export All Revenue
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Link to="/admin/events/create">
                <Button>
                  <Calendar className="w-4 h-4 mr-2" />
                  Create Event
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Calendar}
            label="Total Events"
            value={stats.totalEvents}
            subValue={`${stats.upcomingEvents} upcoming`}
            color="blue"
            loading={loading}
          />
          <StatCard
            icon={Users}
            label="Total Registrations"
            value={stats.totalRegistrations.toLocaleString()}
            color="green"
            loading={loading}
          />
          <StatCard
            icon={Ticket}
            label="Tickets Sold"
            value={stats.totalTickets.toLocaleString()}
            subValue={`${stats.checkedIn} checked in`}
            color="purple"
            loading={loading}
          />
          <StatCard
            icon={DollarSign}
            label="Total Revenue"
            value={formatCurrency(stats.totalRevenue)}
            color="yellow"
            loading={loading}
          />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                data-testid="search-events"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary"
              data-testid="status-filter"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* Events List */}
        <div className="space-y-4">
          {loading ? (
            // Loading skeleton
            [...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border p-4 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-5 h-5 bg-gray-200 rounded" />
                  <div className="flex-1">
                    <div className="h-5 w-48 bg-gray-200 rounded mb-2" />
                    <div className="h-4 w-32 bg-gray-100 rounded" />
                  </div>
                  <div className="flex gap-6">
                    <div className="h-8 w-16 bg-gray-200 rounded" />
                    <div className="h-8 w-16 bg-gray-200 rounded" />
                    <div className="h-8 w-24 bg-gray-200 rounded" />
                  </div>
                </div>
              </div>
            ))
          ) : filteredEvents.length === 0 ? (
            <div className="bg-white rounded-xl border p-12 text-center">
              <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No events found</h3>
              <p className="text-gray-500 mb-6">
                {searchQuery || statusFilter !== 'all' 
                  ? 'Try adjusting your filters'
                  : 'Create your first event to get started'}
              </p>
              <Link to="/admin/events/create">
                <Button>
                  <Calendar className="w-4 h-4 mr-2" />
                  Create Event
                </Button>
              </Link>
            </div>
          ) : (
            filteredEvents.map((event) => (
              <EventRow
                key={event.id}
                event={event}
                expanded={expandedEvent === event.id}
                onToggle={() => setExpandedEvent(expandedEvent === event.id ? null : event.id)}
                onClone={handleCloneEvent}
                onExport={handleExportRevenue}
              />
            ))
          )}
        </div>

        {/* Quick Links */}
        {!loading && events.length > 0 && (
          <div className="mt-8 bg-white rounded-xl border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link 
                to="/admin/events" 
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Calendar className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-gray-900">All Events</p>
                  <p className="text-sm text-gray-500">Manage events</p>
                </div>
              </Link>
              <Link 
                to="/templates" 
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Settings className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-medium text-gray-900">Templates</p>
                  <p className="text-sm text-gray-500">Form templates</p>
                </div>
              </Link>
              <Link 
                to="/events" 
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Eye className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-gray-900">Public View</p>
                  <p className="text-sm text-gray-500">Events directory</p>
                </div>
              </Link>
              <Link 
                to="/admin/events/create" 
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <TrendingUp className="w-5 h-5 text-yellow-600" />
                <div>
                  <p className="font-medium text-gray-900">New Event</p>
                  <p className="text-sm text-gray-500">Create event</p>
                </div>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizerDashboard;
